import type { Account } from '../types/account.js';

// SINGLE SOURCE OF TRUTH for the accounts-grid "Missing" presence filter.
//
// Every scalar identifier / URL field on the Account that an AM might audit
// for completeness ("which accounts are missing their report URL / IG handle /
// pixel?") lives here, ONCE. The filter type (`AccountMissingFieldKey`), the
// server zod enum (`missingFieldEnum`), and the filter-bar dropdown options all
// derive from this list, so adding a new identifier/URL field to the Account
// can't drift out of sync across the three surfaces — add it here and every
// surface picks it up.
//
// This is deliberately Vite-free (only a type import) so the Express server can
// import it directly, same posture as `allowedEmails.ts`.
//
// What's intentionally NOT here (and why): free-text notes (description /
// targetingNotes / notes / leadSyncingNotes — low-signal as a presence filter),
// the nested billingAddress, audit metadata (createdAt / updatedAt /
// statusChanged*), the internal zernioProfileId link, and the fields that
// already have their own dedicated filter control (accountType, industry,
// status, currency, services, budgets, runs*, mediaSpendPaidBy, the Brand
// filter, the AM select, the Not-connected platform filter). The exhaustive
// per-field audit + its rationale lives in `accountFieldSurfaces.ts`.
export const ACCOUNT_MISSING_FIELDS = [
  // Assignment / commercial identifiers
  { key: 'accountManagerId', label: 'Account Manager' },
  { key: 'salesforceAccountId', label: 'Salesforce ID' },
  // Meta / ad-account identifiers
  { key: 'fbAdAccountId', label: 'FB Ad Account ID' },
  { key: 'fbAdAccountUrl', label: 'FB Ad Account URL' },
  { key: 'defaultInstagramAccountId', label: 'Instagram account ID' },
  { key: 'defaultInstagramAccountName', label: 'Instagram handle' },
  { key: 'defaultPixelId', label: 'Default pixel' },
  { key: 'defaultLeadFormId', label: 'Default lead form' },
  // Google identifiers
  { key: 'googleAdsCustomerId', label: 'Google Ads ID' },
  { key: 'googleAdsUrl', label: 'Google Ads URL' },
  // Client CRM link (Snap GymSales company_id) — the club report's join key.
  { key: 'crmId', label: 'CRM ID' },
  { key: 'googleAnalyticsPropertyId', label: 'GA4 property ID' },
  // Social / web URLs
  { key: 'facebookPageUrl', label: 'Facebook Page' },
  { key: 'instagramUrl', label: 'Instagram URL' },
  { key: 'tiktokUrl', label: 'TikTok' },
  { key: 'googleBusinessProfileUrl', label: 'Google Business' },
  { key: 'websiteUrl', label: 'Website' },
  // Reporting / drive
  { key: 'reportUrl', label: 'Report dashboard' },
  { key: 'clientDriveUrl', label: 'Client Drive' },
  { key: 'internalDriveUrl', label: 'Internal Drive' },
] as const satisfies ReadonlyArray<{ key: keyof Account; label: string }>;

// A row matches the `missing` filter when EVERY listed field is empty (null /
// "" / undefined). Keys are a subset of `keyof Account`.
export type AccountMissingFieldKey = (typeof ACCOUNT_MISSING_FIELDS)[number]['key'];

export const ACCOUNT_MISSING_FIELD_KEYS: AccountMissingFieldKey[] =
  ACCOUNT_MISSING_FIELDS.map((f) => f.key);
